import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Lightweight i18n system — French (default) + English.
 *   - Language stored in URL `?lang=en` and persisted in localStorage.
 *   - `useT()` hook returns a translator function `t(key)`.
 *   - Missing keys fall back to French, then to the key itself.
 *   - `<html lang>` is automatically synchronised with the active language for SEO.
 */

export const SUPPORTED_LANGS = ["fr", "en"];
export const DEFAULT_LANG = "fr";

const DICT = {
  // ---------- Navigation ----------
  "nav.home":      { fr: "Accueil",   en: "Home" },
  "nav.portfolio": { fr: "Portfolio", en: "Portfolio" },
  "nav.shop":      { fr: "Boutique",  en: "Shop" },
  "nav.contact":   { fr: "Contact",   en: "Contact" },
  "nav.menu":      { fr: "Menu",      en: "Menu" },
  "nav.close":     { fr: "Fermer",    en: "Close" },

  // ---------- Hero ----------
  "hero.eyebrow":  { fr: "Photographie · Vidéo · Production", en: "Photography · Video · Production" },
  "hero.tagline":  {
    fr: "Capturer l’instant avec la même exigence, du studio aux terrains les plus extrêmes.",
    en: "Capturing the moment with the same precision, from the studio to the most extreme terrains."
  },
  "hero.tagline.sub": {
    fr: "Photographie professionnelle · Production audiovisuelle.",
    en: "Professional photography · Audiovisual production."
  },

  // ---------- Stats ----------
  "stat.clients":    { fr: "Clients satisfaits",    en: "Happy clients" },
  "stat.countries":  { fr: "Pays parcourus",        en: "Countries visited" },
  "stat.projects":   { fr: "Grands projets",        en: "Major projects" },
  "stat.brands":     { fr: "Marques accompagnées",  en: "Brands supported" },

  // ---------- Services ----------
  "services.eyebrow":  { fr: "Services",      en: "Services" },
  "services.heading1": { fr: "Quatre disciplines,", en: "Four disciplines," },
  "services.heading2": { fr: "une signature",  en: "one signature" },
  "services.photo":    { fr: "Photographie sportive",   en: "Sports photography" },
  "services.photoDesc":{ fr: "Compétitions, athlètes en action, eau vive.", en: "Competitions, athletes in action, whitewater." },
  "services.land":     { fr: "Paysages & expéditions",  en: "Landscapes & expeditions" },
  "services.landDesc": { fr: "Volcans, sommets enneigés, expéditions sauvages.", en: "Volcanoes, snow-capped peaks, wild expeditions." },
  "services.adv":      { fr: "Sports extrêmes",         en: "Extreme sports" },
  "services.advDesc":  { fr: "Ski, surf, alpinisme, vol libre.", en: "Skiing, surfing, mountaineering, paragliding." },
  "services.food":     { fr: "Photographie culinaire",  en: "Culinary photography" },
  "services.foodDesc": { fr: "Restaurants, plats, mise en scène gastronomique.", en: "Restaurants, plates, gourmet mise-en-scène." },

  // ---------- Talents ----------
  "talents.eyebrow":   { fr: "Recommandé par", en: "Endorsed by" },
  "talents.headlineA": { fr: "Champions",      en: "Olympic" },
  "talents.headlineB": { fr: "olympiques",     en: "champions" },
  "talents.intro":     {
    fr: "Deux figures du canoë slalom mondial photographiées et accompagnées en compétition.",
    en: "Two figures of world canoe slalom photographed and accompanied in competition."
  },
  "talents.credit":    { fr: "Photos · Romain Bruneau", en: "Photos · Romain Bruneau" },

  // ---------- Who I am ----------
  "about.eyebrow":  { fr: "Qui suis-je · Romain Bruneau", en: "About · Romain Bruneau" },
  "about.heading":  {
    fr: '« Athlète de haut niveau. Réalisateur de terrain. »',
    en: '“High-level athlete. On-the-ground director.”'
  },
  "about.italic":   { fr: "Réalisateur", en: "Director" },

  // ---------- Portfolio page ----------
  "portfolio.eyebrow":  { fr: "Portfolio",  en: "Portfolio" },
  "portfolio.headlineA":{ fr: "Sélection",  en: "Selected" },
  "portfolio.headlineB":{ fr: "récente.",   en: "works." },
  "portfolio.intro":    {
    fr: "Reportages de sport, paysages d’altitude et productions audiovisuelles. Le terrain comme studio.",
    en: "Sports reportage, mountain landscapes and audiovisual productions. The field as a studio."
  },
  "portfolio.filter.all":      { fr: "Tout",       en: "All" },
  "portfolio.filter.paysage":  { fr: "Paysages",   en: "Landscapes" },
  "portfolio.filter.sport":    { fr: "Sport",      en: "Sports" },
  "portfolio.filter.aventure": { fr: "Aventure",   en: "Adventure" },

  // ---------- Culinaires ----------
  "culinaires.eyebrow":   { fr: "Culinaires",      en: "Culinary" },
  "culinaires.headlineA": { fr: "À table avec",    en: "At the table with" },
  "culinaires.headlineB": { fr: "la lumière",      en: "the light" },
  "culinaires.intro":     {
    fr: "Restaurants, plats signature, ambiances gourmandes. Une mise en scène cinématographique de la cuisine, avec la même exigence que le sport.",
    en: "Restaurants, signature dishes, gourmet ambiances. Cinematic staging of cuisine, with the same precision as sport."
  },
  "culinaires.feature":   { fr: "Plat signature",  en: "Signature dish" },
  "culinaires.kicker":    { fr: "Cuisine",         en: "Cuisine" },
  "culinaires.cta":       { fr: "Voir la galerie culinaire", en: "View the culinary gallery" },

  // ---------- Partners ----------
  "partners.eyebrow":   { fr: "Partenaires",   en: "Partners" },
  "partners.headlineA": { fr: "Ils m’ont fait",en: "Trusted by" },
  "partners.headlineB": { fr: "confiance",     en: "great brands" },
  "partners.sub":       { fr: "Marques · institutions · fédérations internationales", en: "Brands · institutions · international federations" },
  "partners.press.eyebrow": { fr: "Vu dans la presse", en: "Featured in the press" },
  "partners.press.intro":   { fr: "Médias internationaux ayant publié les images de Romain Bruneau.", en: "International outlets that have published Romain Bruneau’s images." },
  "partners.federations":   { fr: "Fédérations", en: "Federations" },

  // ---------- Shop ----------
  "shop.headlineA":  { fr: "Tirages",   en: "Fine-art" },
  "shop.headlineB":  { fr: "limités.",  en: "prints." },
  "shop.intro":      {
    fr: "Tirages d’art en édition limitée, imprimés et expédiés depuis la France. Garantie qualité Fuji.",
    en: "Limited-edition fine-art prints, printed and shipped from France. Fuji quality guarantee."
  },
  "shop.filter.all":      { fr: "Tout",     en: "All" },
  "shop.filter.paysage":  { fr: "Paysages", en: "Landscapes" },
  "shop.filter.sport":    { fr: "Sport",    en: "Sports" },
  "shop.filter.aventure": { fr: "Aventure", en: "Adventure" },
  "shop.size":            { fr: "Format",   en: "Size" },
  "shop.paper":           { fr: "Papier",   en: "Paper" },
  "shop.frame":           { fr: "Cadre",    en: "Frame" },
  "shop.preorder":        { fr: "Précommander", en: "Pre-order" },
  "shop.ship":            { fr: "Livraison", en: "Shipping" },
  "shop.freeShip":        { fr: "Livraison offerte dès 150 €", en: "Free shipping over €150" },
  "shop.limited":         { fr: "Édition limitée", en: "Limited edition" },

  // ---------- Visualizer ----------
  "viz.live":         { fr: "Aperçu interactif · En direct", en: "Interactive preview · Live" },
  "viz.hint":         { fr: "Cliquez · Glissez · Zoomez", en: "Click · Drag · Zoom" },
  "viz.share":        { fr: "Partager mon aperçu", en: "Share my preview" },
  "viz.recenter":     { fr: "Recentrer", en: "Recenter" },
  "viz.viewPhoto":    { fr: "Photo", en: "Photo" },
  "viz.viewRoom":     { fr: "Visualiser dans ma pièce", en: "View in my room" },

  // ---------- Contact ----------
  // ---------- Order modal (checkout step) ----------
  "order.header":       { fr: "Livraison & paiement",     en: "Shipping & payment" },
  "order.summary":      { fr: "Récapitulatif",            en: "Order summary" },
  "order.frame":        { fr: "Cadre",                    en: "Frame" },
  "order.frameStd":     { fr: "Standard",                 en: "Standard" },
  "order.taxIncl":      { fr: "TVA incluse · livraison suivie", en: "VAT included · tracked delivery" },
  "order.fullName":     { fr: "Nom complet",              en: "Full name" },
  "order.emailLabel":   { fr: "E-mail",                   en: "Email" },
  "order.phone":        { fr: "Téléphone",                en: "Phone" },
  "order.street":       { fr: "Adresse · rue & numéro",   en: "Address · street & number" },
  "order.postal":       { fr: "Code postal",              en: "Postal code" },
  "order.city":         { fr: "Ville",                    en: "City" },
  "order.country":      { fr: "Pays",                     en: "Country" },
  "order.redirecting":  { fr: "Redirection…",             en: "Redirecting…" },
  "order.pay":          { fr: "Payer",                    en: "Pay" },
  "order.securedBy":    {
    fr: "Paiement sécurisé Stripe · Cartes Bancaires (CB), Apple Pay, Google Pay, Visa, Mastercard. Expédition sous 2 à 5 jours ouvrés après paiement.",
    en: "Secure Stripe payment · Credit cards, Apple Pay, Google Pay, Visa, Mastercard. Shipped within 2 to 5 working days after payment."
  },
  "order.err.default":  { fr: "Une erreur est survenue",  en: "An error occurred" },
  "order.err.noUrl":    { fr: "Pas d'URL de paiement",    en: "No payment URL received" },
  "order.err.payment":  { fr: "Erreur de paiement",       en: "Payment error" },
  "country.france":     { fr: "France",       en: "France" },
  "country.belgium":    { fr: "Belgique",     en: "Belgium" },
  "country.switzerland":{ fr: "Suisse",       en: "Switzerland" },
  "country.luxembourg": { fr: "Luxembourg",   en: "Luxembourg" },
  "country.germany":    { fr: "Allemagne",    en: "Germany" },
  "country.spain":      { fr: "Espagne",      en: "Spain" },
  "country.italy":      { fr: "Italie",       en: "Italy" },
  "country.netherlands":{ fr: "Pays-Bas",     en: "Netherlands" },
  "country.uk":         { fr: "Royaume-Uni",  en: "United Kingdom" },
  "country.other":      { fr: "Autre",        en: "Other" },

  // ---------- Paper descriptions (used in Boutique modal + card) ----------
  "paper.pearl.desc":  { fr: "Semi-brillant irisé · couleurs vives. Recommandé pour les paysages.", en: "Semi-gloss, iridescent finish · vivid colours. Recommended for landscapes." },
  "paper.velvet.desc": { fr: "Mat velouté · idéal pour les ambiances et le noir & blanc.",       en: "Soft matte velvet finish · ideal for moody scenes and black & white." },
  "paper.metallic.desc":{ fr: "Fini métallique · reflets ultra vifs. Recommandé pour paysages spectaculaires.", en: "Metallic finish · vivid reflections. Recommended for dramatic landscapes." },

  // ---------- Room visualizer ----------
  "room.eyebrow":    { fr: "Simulation à l'échelle",  en: "Scale simulation" },
  "room.title":      { fr: "Visualiser dans ma pièce", en: "See in my room" },
  "room.subtitle":   { fr: "Choisissez un décor de référence et voyez le format à taille réelle.", en: "Pick a reference scene and see the print at real scale." },
  "room.livingroom": { fr: "Salon",       en: "Living room" },
  "room.bedroom":    { fr: "Chambre",     en: "Bedroom" },
  "room.office":     { fr: "Bureau",      en: "Office" },
  "room.hallway":    { fr: "Couloir",     en: "Hallway" },
  "room.frameOn":    { fr: "Avec cadre",  en: "Framed" },
  "room.frameOff":   { fr: "Sans cadre",  en: "Unframed" },
  "room.wallHint":   { fr: "Mur en 2,5 m · humain 1,70 m", en: "Wall 2.5 m · person 1.70 m" },
  "room.compare":    { fr: "Comparer les formats",    en: "Compare formats" },

  "contact.eyebrow":   { fr: "Contact",     en: "Contact" },
  "contact.headlineA": { fr: "Discutons",   en: "Let’s talk about" },
  "contact.headlineB": { fr: "de votre projet", en: "your project" },
  "contact.pageHeroA": { fr: "Écrivons une", en: "Let’s plan an" },
  "contact.pageHeroIt":{ fr: "expédition",   en: "expedition" },
  "contact.intro2":    {
    fr: "Je réponds personnellement à chaque message sous 48 heures. Décrivez votre projet, le terrain et les délais. Je reviens vers vous avec une première lecture éditoriale.",
    en: "I answer every message personally within 48 hours. Describe your project, the terrain and the timeline. I will get back to you with an initial editorial reading."
  },
  "contact.label.email":     { fr: "Email",     en: "Email" },
  "contact.label.phone":     { fr: "Téléphone", en: "Phone" },
  "contact.label.socials":   { fr: "Réseaux",   en: "Socials" },
  "contact.label.base":      { fr: "Base",      en: "Based in" },
  "contact.baseValue":       { fr: "Pau, France · Mobile en Europe et au-delà", en: "Pau, France · Available across Europe and beyond" },
  "contact.label.legal":     { fr: "Mentions légales", en: "Legal notice" },
  "contact.form.sent.title": { fr: "Message envoyé",   en: "Message sent" },
  "contact.form.sent.sub":   { fr: "Je reviens vers vous sous 48 heures.", en: "I will get back to you within 48 hours." },
  "contact.form.sent.again": { fr: "Envoyer un autre message", en: "Send another message" },
  "contact.form.name":       { fr: "Nom",           en: "Name" },
  "contact.form.namePh":     { fr: "Votre nom",     en: "Your name" },
  "contact.form.emailPh":    { fr: "vous@exemple.com", en: "you@example.com" },
  "contact.form.type":       { fr: "Type de projet", en: "Project type" },
  "contact.form.message":    { fr: "Message",       en: "Message" },
  "contact.form.messagePh":  { fr: "Décrivez le projet, le terrain et les délais.", en: "Describe the project, the terrain and the timeline." },
  "contact.form.submit":     { fr: "Envoyer le message", en: "Send message" },
  "contact.form.submitting": { fr: "Envoi…",        en: "Sending…" },
  "contact.toast.success":   { fr: "Message envoyé. Je reviens vers vous sous 48h.", en: "Message sent. I will get back to you within 48 hours." },
  "contact.toast.error":     { fr: "Erreur lors de l'envoi. Réessayez ou écrivez directement par email.", en: "Sending failed. Please try again or email me directly." },
  "contact.type.expedition": { fr: "Expédition",           en: "Expedition" },
  "contact.type.doc":        { fr: "Documentaire / Film",  en: "Documentary / Film" },
  "contact.type.sport":      { fr: "Sport & Événement",    en: "Sports & Events" },
  "contact.type.edito":      { fr: "Édition / Magazine",   en: "Editorial / Magazine" },
  "contact.type.print":      { fr: "Tirage d'art",         en: "Art print" },
  "contact.type.other":      { fr: "Autre",                en: "Other" },
  "contact.intro":     {
    fr: "Une compétition, un reportage, une campagne ou un tirage ? Écrivez-nous, réponse sous 48h.",
    en: "A competition, a reportage, a campaign or a print? Drop us a line, reply within 48h."
  },
  "contact.name":      { fr: "Nom complet", en: "Full name" },
  "contact.email":     { fr: "E-mail",      en: "Email" },
  "contact.message":   { fr: "Votre message", en: "Your message" },
  "contact.send":      { fr: "Envoyer", en: "Send" },
  "contact.sent":      { fr: "Message envoyé. Merci !", en: "Message sent. Thank you!" },
  "contact.error":     { fr: "Une erreur est survenue.", en: "Something went wrong." },

  // ---------- Footer ----------
  "footer.bio":        {
    fr: "Athlète de haut niveau et réalisateur audiovisuel français. Kayak slalom, ultra-running et photographie d'aventure.",
    en: "French high-level athlete and audiovisual director. Canoe slalom, ultra-running and adventure photography."
  },
  "footer.menu":       { fr: "Menu",       en: "Menu" },
  "footer.social":     { fr: "Suivre",     en: "Follow" },
  "footer.rights":     { fr: "Tous droits réservés.", en: "All rights reserved." },

  // ---------- Page <title> / meta description ----------
  "meta.home.title":   {
    fr: "Romain Bruneau · Photographe & réalisateur sportif",
    en: "Romain Bruneau · Sports photographer & director"
  },
  "meta.home.desc":    {
    fr: "Photographe et réalisateur français spécialisé dans le sport, le paysage et la production audiovisuelle. Athlète de haut niveau, basé à Pau.",
    en: "French sports photographer and director specialising in sports, landscape and audiovisual production. High-level athlete based in Pau."
  },
  "meta.portfolio.title": {
    fr: "Portfolio · Romain Bruneau",
    en: "Portfolio · Romain Bruneau"
  },
  "meta.portfolio.desc":  {
    fr: "Sélection de photographies signées Romain Bruneau : kayak slalom, paysages d'altitude, sports extrêmes et productions audiovisuelles.",
    en: "Selected photographs by Romain Bruneau: canoe slalom, mountain landscapes, extreme sports and audiovisual productions."
  },
  "meta.shop.title":   {
    fr: "Boutique · Tirages d'art Romain Bruneau",
    en: "Shop · Romain Bruneau fine-art prints"
  },
  "meta.shop.desc":    {
    fr: "Tirages d'art photographiques en édition limitée. Paysages volcaniques, kayak olympique, sports extrêmes. Impression Fuji depuis la France.",
    en: "Limited-edition photographic fine-art prints. Volcanic landscapes, Olympic kayak, extreme sports. Fuji printing from France."
  },
  "meta.contact.title":{
    fr: "Contact · Romain Bruneau",
    en: "Contact · Romain Bruneau"
  },
  "meta.contact.desc": {
    fr: "Devis pour reportage sportif, production audiovisuelle ou tirage d'art. Réponse sous 48h, Pau et international.",
    en: "Quote for sports reportage, audiovisual production or fine-art print. Reply within 48h, Pau and international."
  },

  // ---------- Video presentation ----------
  "video.eyebrow":   { fr: "Présentation vidéo",   en: "Video presentation" },
  "video.headlineA": { fr: "Présentation",         en: "Video" },
  "video.headlineB": { fr: "vidéo",                en: "presentation" },
  "video.intro":     {
    fr: "Sélection cinématographique en boucle continue. Paysages, compétitions, expéditions.",
    en: "Continuous cinematic selection. Landscapes, competitions, expeditions."
  },
  "video.live":      { fr: "Lecture en direct", en: "Now playing" },

  // ---------- About / Who I am extended ----------
  "about.p1": {
    fr: "Romain Bruneau, 23 ans, kayakiste slalom international, Ironman et ultra-runner. Cinq médailles aux Championnats de France, médailles internationales en Espagne et en Pologne, 43e mondial Ironman.",
    en: "Romain Bruneau, 23, international canoe slalom paddler, Ironman athlete and ultra-runner. Five French championship medals, international medals in Spain and Poland, 43rd at the Ironman World Championship."
  },
  "about.p2": {
    fr: "Je documente l'effort depuis l'intérieur, pas depuis une distance de sécurité. Photographe officiel pour la FFCK lors de la Coupe du Monde de Slalom à Pau.",
    en: "I document effort from the inside, not from a safe distance. Official photographer for the FFCK at the Slalom World Cup in Pau."
  },
  "about.cta":       { fr: "Voir le portfolio", en: "View the portfolio" },

  // ---------- Selection ----------
  "selection.eyebrow":  { fr: "Travaux sélectionnés", en: "Selected works" },
  "selection.title":    { fr: "Sélection",            en: "Selection" },
  "selection.intro":    {
    fr: "Six instants. Six respirations. Le geste, le silence, la lumière. Chaque image est une question posée au regard.",
    en: "Six moments. Six breaths. Gesture, silence, light. Each image is a question posed to the gaze."
  },
  "selection.footer":   { fr: "Photographies · Romain Bruneau", en: "Photographs · Romain Bruneau" },
  "selection.cta":      { fr: "Voir le portfolio complet", en: "View the full portfolio" },

  // Selected works — title + reflective caption per piece
  "sel.1.title":   { fr: "Là où la terre respire",         en: "Where the earth breathes" },
  "sel.1.caption": {
    fr: "Survoler la lave, c'est écouter la mémoire du monde avant les mots.",
    en: "Flying over lava is listening to the world's memory before words.",
  },

  "sel.2.title":   { fr: "Le geste devient légende",       en: "Gesture becomes legend" },
  "sel.2.caption": {
    fr: "Mille répétitions pour une seconde. Et cette seconde appartient à l'eau.",
    en: "A thousand repetitions for one second. And that second belongs to the water.",
  },

  "sel.3.title":   { fr: "Le silence en mètres",           en: "Silence in metres" },
  "sel.3.caption": {
    fr: "La montagne ne crie jamais. Elle se mesure en altitude et en patience.",
    en: "Mountains never shout. They are measured in altitude and patience.",
  },

  "sel.4.title":   { fr: "L'œil patient",                en: "The patient eye" },
  "sel.4.caption": {
    fr: "Avant l'instant décisif, il y a mille secondes de patience.",
    en: "Before the decisive moment, a thousand seconds of patience.",
  },

  "sel.5.title":   { fr: "Là où la peur devient grâce",    en: "Where fear becomes grace" },
  "sel.5.caption": {
    fr: "Le vide n'est pas vide pour ceux qui apprennent à le lire.",
    en: "Emptiness is not empty for those who learn to read it.",
  },

  "sel.6.title":   { fr: "Quand la matière devient lumière", en: "When matter becomes light" },
  "sel.6.caption": {
    fr: "La terre se souvient. Parfois, elle se met à briller pour nous le rappeler.",
    en: "The earth remembers. Sometimes it begins to shine to remind us.",
  },

  // ---------- ABOUT PAGE ----------
  "nav.about": { fr: "À propos", en: "About" },

  "meta.about.title": {
    fr: "À propos · Romain Bruneau, athlète outdoor & photographe d'aventure",
    en: "About · Romain Bruneau, outdoor athlete & adventure photographer"
  },
  "meta.about.desc": {
    fr: "Sportif de haut niveau et photographe outdoor — Romain Bruneau raconte l'effort, l'aventure et la performance avec le regard de celui qui les vit.",
    en: "High-level athlete and outdoor photographer — Romain Bruneau tells stories of effort, adventure and performance through the eyes of someone who truly lives them."
  },

  // Hero
  "about2.hero.eyebrow":  { fr: "À propos · Romain Bruneau",    en: "About · Romain Bruneau" },
  "about2.hero.titleA":   { fr: "Romain",                       en: "Romain" },
  "about2.hero.titleB":   { fr: "Bruneau",                      en: "Bruneau" },
  "about2.hero.role":     {
    fr: "Athlète outdoor · Photographe d'aventure · Créateur d'images authentiques.",
    en: "Outdoor athlete · Adventure photographer · Creator of authentic visual stories."
  },
  "about2.hero.lead": {
    fr: "Sportif de haut niveau, photographe et passionné d'aventure, Romain capture l'intensité du terrain avec le regard de quelqu'un qui le vit vraiment.",
    en: "As a high-level athlete, photographer and adventure-driven creator, Romain captures the intensity of the field through the eyes of someone who truly lives it."
  },
  "about2.hero.cta":      { fr: "Découvrir mon histoire",  en: "Discover my story" },
  "about2.hero.scroll":   { fr: "Défiler",                 en: "Scroll" },
  "about2.hero.tag1":     { fr: "Athlète",                 en: "Athlete" },
  "about2.hero.tag2":     { fr: "Photographe",             en: "Photographer" },
  "about2.hero.tag3":     { fr: "Aventurier",              en: "Adventurer" },
  "about2.showreel.enableSound": { fr: "Activer le son",   en: "Enable sound" },
  "about2.showreel.muteSound":   { fr: "Couper le son",    en: "Mute sound" },

  // Category display labels (used on Portfolio / Boutique cards + lightbox meta)
  "cat.paysage":  { fr: "Paysage",  en: "Landscape" },
  "cat.sport":    { fr: "Sport",    en: "Sport" },
  "cat.aventure": { fr: "Aventure", en: "Adventure" },

  // ---------- Boutique (Shop) hero + strip + cards + modal ----------
  "shop.eyebrow":         { fr: "Boutique · Tirages d'art", en: "Shop · Art prints" },
  "shop.heroA":           { fr: "Emportez la",             en: "Bring the" },
  "shop.heroIt":          { fr: "course",                  en: "race" },
  "shop.heroB":           { fr: "chez vous.",              en: "home." },
  "shop.heroLead": {
    fr: "Tirages sur papier argentique Fujifilm, fabriqué en France. Vendus sans cadre, l'encadrement reste au choix du client.",
    en: "Silver-halide Fujifilm prints, made in France. Sold unframed — you choose the framing."
  },
  "shop.freeShipping":    { fr: "Livraison offerte dès",   en: "Free shipping from" },
  "shop.strip.paper":     { fr: "Papier",                  en: "Paper" },
  "shop.strip.paperVal":  { fr: "Fuji DPII · Pearl ou Velvet", en: "Fuji DPII · Pearl or Velvet" },
  "shop.strip.production":{ fr: "Production",              en: "Production" },
  "shop.strip.productionVal": { fr: "labaphoto.fr · France", en: "labaphoto.fr · France" },
  "shop.strip.delay":     { fr: "Délai",                   en: "Turnaround" },
  "shop.strip.delayVal":  { fr: "5 – 7 jours ouvrés",      en: "5 – 7 working days" },
  "shop.card.paperLine":  { fr: "Tirage argentique · Fujifilm", en: "Silver-halide print · Fujifilm" },
  "shop.card.from":       { fr: "À partir de",             en: "From" },
  "shop.card.orderCTA":   { fr: "Commander",               en: "Order" },
  "shop.bottom.paper.eyebrow": { fr: "Notre papier",       en: "Our paper" },
  "shop.bottom.paper.title":   { fr: "Fuji DPII · argentique", en: "Fuji DPII · silver-halide" },
  "shop.bottom.paper.text": {
    fr: "Papier argentique Fujifilm fabriqué en France depuis 1972. Restitution fidèle des couleurs et du grain, longévité d'archive.",
    en: "Fujifilm silver-halide paper, made in France since 1972. Faithful colour and grain reproduction, archival longevity."
  },
  "shop.bottom.noframe.eyebrow": { fr: "Sans cadre",       en: "Unframed" },
  "shop.bottom.noframe.title":   { fr: "Tirage seul",      en: "Print only" },
  "shop.bottom.noframe.text": {
    fr: "La boutique vend uniquement le tirage. L'encadrement reste au choix de chacun, pour ajuster au lieu et au budget.",
    en: "The shop sells the print only. Framing remains your choice, to match your space and budget."
  },
  "shop.bottom.limited.eyebrow": { fr: "Édition limitée",  en: "Limited edition" },
  "shop.bottom.limited.title":   { fr: "60 × 90 et 80 × 120", en: "60 × 90 and 80 × 120" },
  "shop.bottom.limited.text": {
    fr: "Disponibles en édition limitée, numérotés et signés, 30 exemplaires maximum, certificat d'authenticité inclus.",
    en: "Available as a limited edition, numbered and signed, 30 copies max, certificate of authenticity included."
  },
  "shop.modal.eyebrow":     { fr: "Tirage d'art · Romain Bruneau", en: "Art print · Romain Bruneau" },
  "shop.modal.paperNote":   { fr: "Tirage sur papier argentique Fujifilm · fabriqué en France", en: "Fujifilm silver-halide print · made in France" },
  "shop.modal.format":      { fr: "Format",                 en: "Format" },
  "shop.modal.paper":       { fr: "Papier",                 en: "Paper" },
  "shop.modal.limited":     { fr: "Édition limitée · numérotée et signée", en: "Limited edition · numbered and signed" },
  "shop.modal.limitedNote": { fr: "30 exemplaires · certificat d'authenticité", en: "30 copies · certificate of authenticity" },
  "shop.modal.print":       { fr: "Tirage",                 en: "Print" },
  "shop.modal.shipping":    { fr: "Livraison",              en: "Shipping" },
  "shop.modal.free":        { fr: "Offerte",                en: "Free" },
  "shop.modal.total":       { fr: "Total",                  en: "Total" },
  "shop.modal.remaining":   { fr: "Plus que",               en: "Only" },
  "shop.modal.forFreeShip": { fr: "pour la livraison offerte", en: "away from free shipping" },
  "shop.modal.addCart":     { fr: "Ajouter au panier",      en: "Add to cart" },
  "shop.modal.viewPhoto":   { fr: "Photo",                  en: "Photo" },
  "shop.modal.viewRoom":    { fr: "Visualiser dans ma pièce", en: "See in my room" },
  "shop.modal.tryIt":       { fr: "← Essayez · Interactif", en: "← Try it · Interactive" },
  "shop.modal.footNote": {
    fr: "Tirage seul, encadrement au choix du client. Production en 3-4 jours ouvrés, livraison Colissimo sous 2-3 jours.",
    en: "Print only, framing at customer's discretion. Production in 3-4 working days, Colissimo shipping in 2-3 days."
  },

  // Story
  "about2.story.eyebrow": { fr: "Mon parcours",  en: "My journey" },
  "about2.story.title":   { fr: "Le terrain comme",  en: "The field as" },
  "about2.story.titleIt": { fr: "première école",    en: "the first school" },
  "about2.story.p1": {
    fr: "Sportif de haut niveau, j'évolue depuis plusieurs années dans des environnements où l'engagement, la performance et l'aventure font partie du quotidien.",
    en: "As a high-level athlete, I have spent several years evolving in environments where commitment, performance and adventure are part of everyday life."
  },
  "about2.story.p2": {
    fr: "L'alpinisme, les sports d'endurance, le parapente et les activités outdoor m'ont appris à m'adapter rapidement, à anticiper et à rester efficace dans les situations les plus exigeantes. Cette expérience constitue aujourd'hui un véritable atout dans mon métier de photographe.",
    en: "Mountaineering, endurance sports, paragliding and outdoor activities have taught me how to adapt quickly, anticipate challenges and remain efficient in the most demanding situations. Today, this experience is a real strength in my work as a photographer."
  },
  "about2.story.p3": {
    fr: "Parce que je partage les mêmes terrains, les mêmes contraintes et les mêmes objectifs que les athlètes que je photographie, je suis capable de comprendre leur réalité et de capturer l'intensité de leurs performances avec authenticité.",
    en: "Because I share the same terrain, constraints and goals as the athletes I photograph, I am able to understand their reality and capture the intensity of their performance with authenticity."
  },
  "about2.story.p4": {
    fr: "Bien que spécialisé dans l'univers du sport, de l'aventure et de l'outdoor, j'accompagne également des marques, des entreprises et des porteurs de projets souhaitant valoriser leur image à travers des photographies fortes et sincères.",
    en: "While I specialize in the world of sport, adventure and outdoor photography, I also work with brands, companies and project owners who want to elevate their image through strong, sincere and impactful visuals."
  },
  "about2.story.signature": {
    fr: "Mon ambition est simple : raconter des histoires visuelles qui inspirent, marquent les esprits et mettent en lumière l'énergie réelle de chaque projet.",
    en: "My ambition is simple: to tell visual stories that inspire, leave a lasting impression and reveal the true energy behind every project."
  },

  // Vision pillars
  "about2.vision.eyebrow":  { fr: "Une vision",            en: "A vision" },
  "about2.vision.title":    { fr: "née du",                en: "born from the" },
  "about2.vision.titleIt":  { fr: "terrain",               en: "field" },
  "about2.vision.intro": {
    fr: "Trois piliers qui définissent ma manière de photographier le sport et l'aventure.",
    en: "Three pillars that define my way of photographing sport and adventure."
  },
  "about2.vision.p1.title": { fr: "Comprendre l'effort",    en: "Understanding the effort" },
  "about2.vision.p1.text":  {
    fr: "Je connais les exigences physiques et mentales du sport. Cela me permet de saisir le moment juste, là où d'autres voient seulement de l'action.",
    en: "I know the physical and mental demands of sport. That lets me catch the right moment, where others only see action."
  },
  "about2.vision.p2.title": { fr: "Anticiper l'action",     en: "Anticipating the action" },
  "about2.vision.p2.text":  {
    fr: "Une décennie d'expérience en sport et en montagne m'apprend à prévoir le mouvement, le risque et la dynamique d'une scène.",
    en: "A decade of experience in sport and the mountains teaches me to anticipate movement, risk and the dynamics of a scene."
  },
  "about2.vision.p3.title": { fr: "Capturer l'authenticité", en: "Capturing authenticity" },
  "about2.vision.p3.text":  {
    fr: "Pas de mise en scène artificielle. Mes images racontent l'émotion réelle, telle qu'elle s'écrit sur le terrain.",
    en: "No artificial staging. My images tell the real emotion, the way it is written on the ground."
  },

  // Palmarès
  "about2.palm.eyebrow": { fr: "Palmarès sportif", en: "Sporting achievements" },
  "about2.palm.title":   { fr: "Une carrière",     en: "A career" },
  "about2.palm.titleIt": { fr: "vécue, pas observée", en: "lived, not observed" },
  "about2.palm.intro": {
    fr: "Trois disciplines. Une décennie d'engagement. Et la conviction que connaître l'effort de l'intérieur change la façon de le photographier.",
    en: "Three disciplines. A decade of commitment. And the conviction that knowing effort from the inside changes the way you photograph it."
  },

  "about2.palm.c1.label":  { fr: "Canoë-kayak",  en: "Canoe & kayak" },
  "about2.palm.c1.0":      { fr: "1 médaille européenne · Pologne", en: "1 European medal · Poland" },
  "about2.palm.c1.1":      { fr: "3 médailles internationales · France & Espagne", en: "3 international medals · France & Spain" },
  "about2.palm.c1.2":      { fr: "5 médailles aux Championnats de France", en: "5 medals at the French Championships" },
  "about2.palm.c1.3":      { fr: "2 médailles d'argent",   en: "2 silver medals" },
  "about2.palm.c1.4":      { fr: "3 médailles de bronze",  en: "3 bronze medals" },

  "about2.palm.c2.label":  { fr: "Triathlon & Endurance",  en: "Triathlon & Endurance" },
  "about2.palm.c2.0":      { fr: "4ᵉ Français sur épreuve Ironman", en: "4th French athlete in an Ironman event" },
  "about2.palm.c2.1":      { fr: "43ᵉ mondial",            en: "43rd worldwide" },
  "about2.palm.c2.2":      { fr: "Sélectionné aux Championnats du Monde Ironman 70.3 · Nouvelle-Zélande", en: "Selected for the Ironman 70.3 World Championship · New Zealand" },
  "about2.palm.c2.3":      { fr: "Double participation aux Championnats d'Europe", en: "Two participations at the European Championships" },
  "about2.palm.c2.4":      { fr: "Dolomitenmann · Red Bull · 35ᵉ mondial", en: "Dolomitenmann · Red Bull · 35th worldwide" },
  "about2.palm.c2.5":      { fr: "Ultra-trail de 210 km · 14 000 m D+", en: "210 km ultra-trail · 14,000 m of positive elevation" },
  "about2.palm.c2.6":      { fr: "Marathon des Sables Legendary · 270 km en autosuffisance", en: "Marathon des Sables Legendary · 270 km self-supported" },

  "about2.palm.c3.label":  { fr: "Alpinisme & Montagne",   en: "Mountaineering & Mountains" },
  "about2.palm.c3.0":      { fr: "Ascension du Mont Blanc · 4 807 m · en one push", en: "Mont Blanc ascent · 4,807 m · in one push" },
  "about2.palm.c3.1":      { fr: "Mont Blanc du Tacul · 4 248 m · en para-alpinisme", en: "Mont Blanc du Tacul · 4,248 m · in para-alpinism" },
  "about2.palm.c3.2":      { fr: "Plus haut sommet des Pyrénées", en: "Highest summit of the Pyrenees" },
  "about2.palm.c3.3":      { fr: "Plus haut sommet d'Allemagne",  en: "Highest summit of Germany" },
  "about2.palm.c3.4":      { fr: "Ascensions majeures · Alpes & Pyrénées", en: "Major ascents · Alps & Pyrenees" },

  // Gallery preview
  "about2.gallery.eyebrow": { fr: "Sélection visuelle", en: "Visual selection" },
  "about2.gallery.title":   { fr: "Le terrain,",        en: "The field," },
  "about2.gallery.titleIt": { fr: "image par image",    en: "image by image" },
  "about2.gallery.cta":     { fr: "Explorer le portfolio", en: "Explore the portfolio" },

  // Showreel
  "about2.reel.eyebrow": { fr: "Showreel",           en: "Showreel" },
  "about2.reel.title":   { fr: "Moments vécus.",     en: "Moments lived." },
  "about2.reel.titleIt": { fr: "Histoires capturées.", en: "Stories captured." },
  "about2.reel.intro": {
    fr: "Une sélection cinématographique au plus près de l'action. Sans filtre, sans décor, sans mise en scène.",
    en: "A cinematic selection, as close to the action as possible. No filter, no set, no staging."
  },

  // For brands
  "about2.brands.eyebrow": { fr: "Pour qui ?",   en: "For whom?" },
  "about2.brands.title":   { fr: "Des marques,",  en: "Brands," },
  "about2.brands.titleIt": { fr: "des athlètes, des récits.", en: "athletes, stories." },
  "about2.brands.intro": {
    fr: "J'accompagne les athlètes, les marques outdoor, les entreprises et les porteurs de projets qui souhaitent créer des images fortes, sincères et mémorables. De la performance sportive aux aventures en pleine nature, chaque projet est pensé comme une histoire à raconter avec justesse.",
    en: "I work with athletes, outdoor brands, companies and project owners who want to create strong, sincere and memorable visuals. From sports performance to outdoor adventures, every project is approached as a story to be told with precision and authenticity."
  },
  "about2.brands.tag.1":   { fr: "Athlètes",         en: "Athletes" },
  "about2.brands.tag.2":   { fr: "Marques outdoor",  en: "Outdoor brands" },
  "about2.brands.tag.3":   { fr: "Marques sportives",en: "Sports brands" },
  "about2.brands.tag.4":   { fr: "Tourisme & aventure", en: "Tourism & adventure" },
  "about2.brands.tag.5":   { fr: "Entreprises",      en: "Companies" },
  "about2.brands.tag.6":   { fr: "Créateurs",        en: "Creators" },
  "about2.brands.tag.7":   { fr: "Événements",       en: "Events" },
  "about2.brands.tag.8":   { fr: "Expéditions",      en: "Expedition teams" },

  // Final CTA
  "about2.cta.title": {
    fr: "Vous avez un projet, une aventure ou une histoire à raconter ?",
    en: "Have a project, an adventure or a story to tell?"
  },
  "about2.cta.sub":   { fr: "Créons des images qui marquent.", en: "Let's create visuals that leave a mark." },
  "about2.cta.btn":   { fr: "Me contacter",                    en: "Contact me" },
};

const LangContext = createContext({ lang: DEFAULT_LANG, setLang: () => {} });

const readInitialLang = () => {
  if (typeof window === "undefined") return DEFAULT_LANG;
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("lang");
  if (fromUrl && SUPPORTED_LANGS.includes(fromUrl)) return fromUrl;
  const stored = window.localStorage.getItem("rb_lang");
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  return DEFAULT_LANG;
};

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(readInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
    try { window.localStorage.setItem("rb_lang", lang); } catch { /* localStorage unavailable */ }
    // sync URL — keep current path, replace lang search param
    const url = new URL(window.location.href);
    if (lang === DEFAULT_LANG) url.searchParams.delete("lang");
    else url.searchParams.set("lang", lang);
    const newUrl = url.pathname + (url.search ? url.search : "") + url.hash;
    if (newUrl !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState(null, "", newUrl);
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED_LANGS.includes(next)) setLangState(next);
  }, []);

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);

export const useT = () => {
  const { lang } = useLang();
  return (key) => {
    const entry = DICT[key];
    if (!entry) return key;
    return entry[lang] || entry[DEFAULT_LANG] || key;
  };
};

/** Set page <title> and meta description for the current language. */
export const usePageMeta = ({ titleKey, descKey, path }) => {
  const { lang } = useLang();
  useEffect(() => {
    const t = (k) => DICT[k]?.[lang] || DICT[k]?.[DEFAULT_LANG] || "";
    const title = t(titleKey);
    const desc = t(descKey);
    if (title) document.title = title;
    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.tag).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content);
    };
    ensureMeta('meta[name="description"]', { tag: { name: "description" }, content: desc });
    ensureMeta('meta[property="og:title"]', { tag: { property: "og:title" }, content: title });
    ensureMeta('meta[property="og:description"]', { tag: { property: "og:description" }, content: desc });
    ensureMeta('meta[property="og:locale"]', { tag: { property: "og:locale" }, content: lang === "en" ? "en_US" : "fr_FR" });
    ensureMeta('meta[name="twitter:title"]', { tag: { name: "twitter:title" }, content: title });
    ensureMeta('meta[name="twitter:description"]', { tag: { name: "twitter:description" }, content: desc });

    // hreflang alternates
    const removeOld = () => document.head.querySelectorAll('link[data-hreflang]').forEach(n => n.remove());
    removeOld();
    const origin = window.location.origin;
    SUPPORTED_LANGS.forEach((code) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", code);
      link.setAttribute("data-hreflang", code);
      const u = new URL(origin + (path || window.location.pathname));
      if (code !== DEFAULT_LANG) u.searchParams.set("lang", code);
      link.setAttribute("href", u.toString());
      document.head.appendChild(link);
    });
    // x-default
    const xd = document.createElement("link");
    xd.setAttribute("rel", "alternate");
    xd.setAttribute("hreflang", "x-default");
    xd.setAttribute("data-hreflang", "x-default");
    xd.setAttribute("href", origin + (path || window.location.pathname));
    document.head.appendChild(xd);

    // canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const cu = new URL(origin + (path || window.location.pathname));
    if (lang !== DEFAULT_LANG) cu.searchParams.set("lang", lang);
    canonical.setAttribute("href", cu.toString());
  }, [lang, titleKey, descKey, path]);
};

export default DICT;
